import { useState, useMemo } from "react";

export function useSIPCalculator() {
  const [sipMonthly, setSipMonthly] = useState(10000);
  const [sipRate, setSipRate] = useState(12);
  const [sipYears, setSipYears] = useState(10);
  const [sipGrowthType, setSipGrowthType] = useState('none');
  const [sipGrowthPercent, setSipGrowthPercent] = useState(0);
  const [sipGrowthHalfYearly, setSipGrowthHalfYearly] = useState(1000);
  const [sipGrowthYearly, setSipGrowthYearly] = useState(1000);
  const [sipGrowthExpanded, setSipGrowthExpanded] = useState(false);

  const result = useMemo(() => {
    const monthlyRate = sipRate / 12 / 100;
    const months = sipYears * 12;
    let futureValue = 0, totalInvested = 0, currentMonthly = sipMonthly;
    if (sipGrowthType === 'none') {
      futureValue = sipMonthly * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate);
      totalInvested = sipMonthly * months;
    } else if (sipGrowthType === 'percent') {
      for (let year = 0; year < sipYears; year++) { const ym = sipMonthly * Math.pow(1 + sipGrowthPercent / 100, year); for (let m = 0; m < 12; m++) { const mr = months - (year * 12 + m); futureValue += ym * Math.pow(1 + monthlyRate, mr); totalInvested += ym; } }
    } else if (sipGrowthType === 'halfYearly') {
      for (let h = 0; h < sipYears * 2; h++) { currentMonthly = sipMonthly + (sipGrowthHalfYearly * h); for (let m = 0; m < 6; m++) { const mr = months - (h * 6 + m); if (mr > 0) { futureValue += currentMonthly * Math.pow(1 + monthlyRate, mr); totalInvested += currentMonthly; } } }
    } else if (sipGrowthType === 'yearly') {
      for (let y = 0; y < sipYears; y++) { currentMonthly = sipMonthly + (sipGrowthYearly * y); for (let m = 0; m < 12; m++) { const mr = months - (y * 12 + m); futureValue += currentMonthly * Math.pow(1 + monthlyRate, mr); totalInvested += currentMonthly; } }
    }
    const growthMultiple = totalInvested > 0 ? (futureValue / totalInvested).toFixed(2) : 0;
    return { futureValue, totalInvested, wealthGained: futureValue - totalInvested, growthMultiple };
  }, [sipMonthly, sipRate, sipYears, sipGrowthType, sipGrowthPercent, sipGrowthHalfYearly, sipGrowthYearly]);

  const chartData = useMemo(() => Array.from({ length: sipYears }, (_, i) => {
    const year = i + 1; const months = year * 12; const mr = sipRate / 12 / 100;
    const value = sipMonthly * ((Math.pow(1 + mr, months) - 1) / mr) * (1 + mr);
    return { year: `Year ${year}`, invested: sipMonthly * months, value: Math.round(value) };
  }), [sipMonthly, sipRate, sipYears]);

  return { sipMonthly, setSipMonthly, sipRate, setSipRate, sipYears, setSipYears, sipGrowthType, setSipGrowthType, sipGrowthPercent, setSipGrowthPercent, sipGrowthHalfYearly, setSipGrowthHalfYearly, sipGrowthYearly, setSipGrowthYearly, sipGrowthExpanded, setSipGrowthExpanded, result, chartData };
}

export function useEMICalculator() {
  const [loanAmount, setLoanAmount] = useState(1000000);
  const [loanRate, setLoanRate] = useState(8.5);
  const [loanYears, setLoanYears] = useState(20);

  const result = useMemo(() => {
    const mr = loanRate / 12 / 100; const months = loanYears * 12;
    const emi = loanAmount * mr * Math.pow(1 + mr, months) / (Math.pow(1 + mr, months) - 1);
    const totalPayment = emi * months;
    return { emi, totalPayment, totalInterest: totalPayment - loanAmount };
  }, [loanAmount, loanRate, loanYears]);

  const chartData = useMemo(() => [
    { name: 'Principal', value: loanAmount, fill: '#14b8a6' },
    { name: 'Interest', value: Math.round(result.totalInterest), fill: '#ef4444' }
  ], [loanAmount, result.totalInterest]);

  const amortizationTable = useMemo(() => {
    const mr = loanRate / 12 / 100;
    const months = loanYears * 12;
    const emi = loanAmount * mr * Math.pow(1 + mr, months) / (Math.pow(1 + mr, months) - 1);
    let balance = loanAmount;
    const table = [];
    for (let year = 1; year <= loanYears; year++) {
      let yearPrincipal = 0, yearInterest = 0;
      for (let m = 0; m < 12; m++) {
        if (balance <= 0) break;
        const interest = balance * mr;
        const principal = Math.min(emi - interest, balance);
        yearPrincipal += principal;
        yearInterest += interest;
        balance -= principal;
      }
      table.push({ year, principal: Math.round(yearPrincipal), interest: Math.round(yearInterest), totalPayment: Math.round(yearPrincipal + yearInterest), balance: Math.max(0, Math.round(balance)) });
    }
    return table;
  }, [loanAmount, loanRate, loanYears]);

  return { loanAmount, setLoanAmount, loanRate, setLoanRate, loanYears, setLoanYears, result, chartData, amortizationTable };
}

export function useFutureValueCalculator() {
  const [fvPrincipal, setFvPrincipal] = useState(100000);
  const [fvRate, setFvRate] = useState(10);
  const [fvYears, setFvYears] = useState(15);

  const result = useMemo(() => {
    const futureValue = fvPrincipal * Math.pow(1 + fvRate / 100, fvYears);
    return { futureValue, interestEarned: futureValue - fvPrincipal };
  }, [fvPrincipal, fvRate, fvYears]);

  return { fvPrincipal, setFvPrincipal, fvRate, setFvRate, fvYears, setFvYears, result };
}

export function useGoalCalculator() {
  const [goalAmount, setGoalAmount] = useState(5000000);
  const [goalYears, setGoalYears] = useState(10);
  const [goalRate, setGoalRate] = useState(12);

  const result = useMemo(() => {
    const mr = goalRate / 12 / 100; const months = goalYears * 12;
    const monthlySIP = goalAmount / (((Math.pow(1 + mr, months) - 1) / mr) * (1 + mr));
    return { monthlySIP, totalInvestment: monthlySIP * months };
  }, [goalAmount, goalYears, goalRate]);

  return { goalAmount, setGoalAmount, goalYears, setGoalYears, goalRate, setGoalRate, result };
}

export function useRetirementCalculator() {
  const [currentAge, setCurrentAge] = useState(30);
  const [retirementAge, setRetirementAge] = useState(60);
  const [lifeExpectancy, setLifeExpectancy] = useState(85);
  const [monthlyExpense, setMonthlyExpense] = useState(50000);
  const [currentSavings, setCurrentSavings] = useState(500000);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [inflationRate, setInflationRate] = useState(6);
  const [retAssumptionsExpanded, setRetAssumptionsExpanded] = useState(false);
  const [retExistingInvExpanded, setRetExistingInvExpanded] = useState(false);
  const [retGrowthExpanded, setRetGrowthExpanded] = useState(false);
  const [retPostRetirementInflation, setRetPostRetirementInflation] = useState(6);
  const [retReturnsOnKitty, setRetReturnsOnKitty] = useState(8);
  const [retExistingInvAmount, setRetExistingInvAmount] = useState(0);
  const [retExistingInvReturn, setRetExistingInvReturn] = useState(12);
  const [retGrowthType, setRetGrowthType] = useState('none');
  const [retGrowthPercent, setRetGrowthPercent] = useState(2);
  const [retGrowthHalfYearly, setRetGrowthHalfYearly] = useState(1000);
  const [retGrowthYearly, setRetGrowthYearly] = useState(1000);

  const result = useMemo(() => {
    const ytr = retirementAge - currentAge;
    const ry = lifeExpectancy - retirementAge;
    const fme = monthlyExpense * Math.pow(1 + inflationRate / 100, ytr);
    const postRetReturn = retReturnsOnKitty / 100;
    const postRetInflation = retPostRetirementInflation / 100;
    // Real annual return via Fisher equation, then simple monthly division (matches standard Indian retirement calculators)
    const realAnnualReturn = (1 + postRetReturn) / (1 + postRetInflation) - 1;
    const monthlyRealReturn = realAnnualReturn / 12;
    const retirementMonths = ry * 12;
    let corpusNeeded;
    if (monthlyRealReturn > 0) {
      corpusNeeded = fme * ((1 - Math.pow(1 + monthlyRealReturn, -retirementMonths)) / monthlyRealReturn);
    } else {
      corpusNeeded = fme * retirementMonths;
    }
    const futureExistingInv = retExistingInvAmount * Math.pow(1 + retExistingInvReturn / 100, ytr);
    const shortfall = Math.max(0, corpusNeeded - futureExistingInv);
    // Simple monthly rate for SIP calculation (consistent with reference)
    const monthlyRate = expectedReturn / 12 / 100;
    const annualRate = expectedReturn / 100;
    const months = ytr * 12;

    // --- Step-Up SIP logic ---
    // Compute per-rupee FV multiplier for the *initial* monthly SIP (P) and,
    // when applicable, the FV contribution of the ₹ step-up amount (so that
    // shortfall = P * baseMult + incr * incrMult  =>  P = (shortfall - incr*incrMult)/baseMult).
    // Uses annuity-due compounding (invest at start of each month) to match reference.
    let monthlyBaseMultiplier = 0;
    let monthlyIncrMultiplier = 0;
    let yearlyBaseMultiplier = 0;
    let yearlyIncrMultiplier = 0;

    if (months > 0) {
      for (let year = 0; year < ytr; year++) {
        // Monthly SIP step-up factor for this year
        let pctFactor = 1;
        if (retGrowthType === 'percent') pctFactor = Math.pow(1 + retGrowthPercent / 100, year);

        // Yearly SIP step-up factor
        let pctFactorYearly = 1;
        if (retGrowthType === 'percent') pctFactorYearly = Math.pow(1 + retGrowthPercent / 100, year);
        const yearsRemaining = ytr - year;
        const yearlyFV = Math.pow(1 + annualRate, yearsRemaining); // annuity-due: contribute at start of year
        yearlyBaseMultiplier += pctFactorYearly * yearlyFV;
        if (retGrowthType === 'yearly') {
          // Year k contribution = Y + incr*k  → separate the incr term
          yearlyIncrMultiplier += year * yearlyFV;
        }
        if (retGrowthType === 'halfYearly') {
          // Half-yearly step-up: treat as two half-year tranches within the year (incr applied per half)
          // Contribution in half h (0,1,2...): monthly = P + incr*h. Handled in monthly loop below.
        }

        for (let m = 0; m < 12; m++) {
          const monthsRemaining = months - (year * 12 + m);
          const compound = Math.pow(1 + monthlyRate, monthsRemaining);
          const halfIndex = year * 2 + (m >= 6 ? 1 : 0);

          if (retGrowthType === 'percent') {
            monthlyBaseMultiplier += pctFactor * compound;
          } else if (retGrowthType === 'halfYearly') {
            monthlyBaseMultiplier += compound;
            monthlyIncrMultiplier += halfIndex * compound;
          } else if (retGrowthType === 'yearly') {
            monthlyBaseMultiplier += compound;
            monthlyIncrMultiplier += year * compound;
          } else {
            // 'none' — standard SIP
            monthlyBaseMultiplier += compound;
          }
        }
      }
    }

    // Solve for initial Monthly SIP
    let monthlySIPNeeded = 0;
    if (shortfall > 0 && monthlyBaseMultiplier > 0) {
      let monthlyIncrAmount = 0;
      if (retGrowthType === 'halfYearly') monthlyIncrAmount = retGrowthHalfYearly;
      else if (retGrowthType === 'yearly') monthlyIncrAmount = retGrowthYearly;
      monthlySIPNeeded = Math.max(0, (shortfall - monthlyIncrAmount * monthlyIncrMultiplier) / monthlyBaseMultiplier);
    }

    // Solve for initial Yearly SIP (step-up applies for 'percent' and 'yearly'; 'halfYearly' ₹ increment
    // doesn't map cleanly to an annual contribution schedule, so treat it as flat yearly for this output)
    let yearlySIPNeeded = 0;
    if (shortfall > 0 && yearlyBaseMultiplier > 0) {
      let yearlyIncrAmount = 0;
      if (retGrowthType === 'yearly') yearlyIncrAmount = retGrowthYearly * 12; // yearly-equivalent ₹ step-up
      yearlySIPNeeded = Math.max(0, (shortfall - yearlyIncrAmount * yearlyIncrMultiplier) / yearlyBaseMultiplier);
    }

    // One-time lumpsum: no step-up concept applies to a single payment
    const oneTimeLumpsum = shortfall / Math.pow(1 + annualRate, ytr);

    return { yearsToRetirement: ytr, retirementYears: ry, futureMonthlyExpense: fme, corpusNeeded, futureExistingInv, shortfall, monthlySIPNeeded, yearlySIPNeeded, oneTimeLumpsum };
  }, [currentAge, retirementAge, lifeExpectancy, monthlyExpense, expectedReturn, inflationRate, retReturnsOnKitty, retPostRetirementInflation, retExistingInvAmount, retExistingInvReturn, retGrowthType, retGrowthPercent, retGrowthHalfYearly, retGrowthYearly]);

  const chartData = useMemo(() => [
    { name: 'Corpus Needed', value: Math.round(result.corpusNeeded), fill: '#f59e0b' },
    { name: 'Existing Inv FV', value: Math.round(result.futureExistingInv), fill: '#22c55e' },
    { name: 'Shortfall', value: Math.round(result.shortfall), fill: '#ef4444' }
  ], [result]);

  return { currentAge, setCurrentAge, retirementAge, setRetirementAge, lifeExpectancy, setLifeExpectancy, monthlyExpense, setMonthlyExpense, currentSavings, setCurrentSavings, expectedReturn, setExpectedReturn, inflationRate, setInflationRate, retAssumptionsExpanded, setRetAssumptionsExpanded, retExistingInvExpanded, setRetExistingInvExpanded, retGrowthExpanded, setRetGrowthExpanded, retPostRetirementInflation, setRetPostRetirementInflation, retReturnsOnKitty, setRetReturnsOnKitty, retExistingInvAmount, setRetExistingInvAmount, retExistingInvReturn, setRetExistingInvReturn, retGrowthType, setRetGrowthType, retGrowthPercent, setRetGrowthPercent, retGrowthHalfYearly, setRetGrowthHalfYearly, retGrowthYearly, setRetGrowthYearly, result, chartData };
}

const LIFE_GOAL_DEFAULTS = {
  "Child Education": { amount: 2000000, inflation: 10, years: 15 },
  "Child Marriage": { amount: 3000000, inflation: 8, years: 20 },
  "Dream Home": { amount: 10000000, inflation: 6, years: 10 },
  "Dream Car": { amount: 6000000, inflation: 8, years: 7 },
  "World Tour": { amount: 1500000, inflation: 8, years: 5 },
  "Emergency Fund": { amount: 1000000, inflation: 6, years: 3 },
  "Other Goal": { amount: 1000000, inflation: 6, years: 5 },
};

export function useLifeGoalCalculator() {
  const [lifeGoalName, setLifeGoalName] = useState("Child Education");
  const [lifeGoalAmount, setLifeGoalAmount] = useState(2000000);
  const [lifeGoalYears, setLifeGoalYears] = useState(15);
  const [lifeGoalInflation, setLifeGoalInflation] = useState(10);
  const [lifeGoalReturn, setLifeGoalReturn] = useState(12);
  const [existingInvAmount, setExistingInvAmount] = useState(0);
  const [existingInvReturn, setExistingInvReturn] = useState(12);
  const [existingInvExpanded, setExistingInvExpanded] = useState(false);
  const [growthExpanded, setGrowthExpanded] = useState(false);
  const [growthType, setGrowthType] = useState('none');
  const [growthPercent, setGrowthPercent] = useState(2);
  const [growthHalfYearly, setGrowthHalfYearly] = useState(1000);
  const [growthYearly, setGrowthYearly] = useState(1000);

  const changeGoal = (name) => {
    setLifeGoalName(name);
    const d = LIFE_GOAL_DEFAULTS[name];
    if (d) { setLifeGoalAmount(d.amount); setLifeGoalInflation(d.inflation); setLifeGoalYears(d.years); }
  };

  const result = useMemo(() => {
    const inflatedGoal = lifeGoalAmount * Math.pow(1 + lifeGoalInflation / 100, lifeGoalYears);
    const futureExisting = existingInvAmount * Math.pow(1 + existingInvReturn / 100, lifeGoalYears);
    const targetAfterExisting = Math.max(0, inflatedGoal - futureExisting);
    const mr = lifeGoalReturn / 12 / 100;
    const months = lifeGoalYears * 12;

    // --- Step-Up SIP solver (same approach as Retirement Calculator) ---
    // shortfall = P * baseMult + incr * incrMult  →  P = (shortfall - incr*incrMult) / baseMult
    let baseMult = 0;
    let incrMult = 0;
    if (months > 0) {
      for (let year = 0; year < lifeGoalYears; year++) {
        const pctFactor = growthType === 'percent' ? Math.pow(1 + growthPercent / 100, year) : 1;
        for (let m = 0; m < 12; m++) {
          const monthsRemaining = months - (year * 12 + m);
          const compound = Math.pow(1 + mr, monthsRemaining); // annuity-due
          const halfIndex = year * 2 + (m >= 6 ? 1 : 0);
          if (growthType === 'percent') {
            baseMult += pctFactor * compound;
          } else if (growthType === 'halfYearly') {
            baseMult += compound;
            incrMult += halfIndex * compound;
          } else if (growthType === 'yearly') {
            baseMult += compound;
            incrMult += year * compound;
          } else {
            baseMult += compound;
          }
        }
      }
    }

    let monthlySIP = 0;
    if (targetAfterExisting > 0 && baseMult > 0) {
      let incrAmount = 0;
      if (growthType === 'halfYearly') incrAmount = growthHalfYearly;
      else if (growthType === 'yearly') incrAmount = growthYearly;
      monthlySIP = Math.max(0, (targetAfterExisting - incrAmount * incrMult) / baseMult);
    }

    // Compute total invested across the step-up schedule (matches the SIP solved above)
    let totalInvestment = 0;
    if (months > 0) {
      for (let year = 0; year < lifeGoalYears; year++) {
        const pctFactor = growthType === 'percent' ? Math.pow(1 + growthPercent / 100, year) : 1;
        for (let m = 0; m < 12; m++) {
          const halfIndex = year * 2 + (m >= 6 ? 1 : 0);
          let contribution = monthlySIP;
          if (growthType === 'percent') contribution = monthlySIP * pctFactor;
          else if (growthType === 'halfYearly') contribution = monthlySIP + growthHalfYearly * halfIndex;
          else if (growthType === 'yearly') contribution = monthlySIP + growthYearly * year;
          totalInvestment += contribution;
        }
      }
    }

    return {
      inflatedGoal,
      monthlySIP,
      totalInvestment,
      wealthGained: inflatedGoal - totalInvestment - futureExisting,
      futureExisting,
      targetAfterExisting,
    };
  }, [lifeGoalAmount, lifeGoalYears, lifeGoalInflation, lifeGoalReturn, existingInvAmount, existingInvReturn, growthType, growthPercent, growthHalfYearly, growthYearly]);

  // Year-by-year accumulation chart data (uses the actual step-up schedule, not a flat SIP)
  const chartData = useMemo(() => {
    const mr = lifeGoalReturn / 12 / 100;
    const data = [];
    let accumulated = 0;
    for (let year = 0; year < lifeGoalYears; year++) {
      const pctFactor = growthType === 'percent' ? Math.pow(1 + growthPercent / 100, year) : 1;
      for (let m = 0; m < 12; m++) {
        const halfIndex = year * 2 + (m >= 6 ? 1 : 0);
        let contribution = result.monthlySIP;
        if (growthType === 'percent') contribution = result.monthlySIP * pctFactor;
        else if (growthType === 'halfYearly') contribution = result.monthlySIP + growthHalfYearly * halfIndex;
        else if (growthType === 'yearly') contribution = result.monthlySIP + growthYearly * year;
        accumulated = (accumulated + contribution) * (1 + mr);
      }
      data.push({ year: `Year ${year + 1}`, value: Math.round(accumulated), goal: Math.round(result.inflatedGoal) });
    }
    return data;
  }, [lifeGoalYears, lifeGoalReturn, growthType, growthPercent, growthHalfYearly, growthYearly, result.monthlySIP, result.inflatedGoal]);

  return { lifeGoalName, setLifeGoalName: changeGoal, lifeGoalAmount, setLifeGoalAmount, lifeGoalYears, setLifeGoalYears, lifeGoalInflation, setLifeGoalInflation, lifeGoalReturn, setLifeGoalReturn, existingInvAmount, setExistingInvAmount, existingInvReturn, setExistingInvReturn, existingInvExpanded, setExistingInvExpanded, growthExpanded, setGrowthExpanded, growthType, setGrowthType, growthPercent, setGrowthPercent, growthHalfYearly, setGrowthHalfYearly, growthYearly, setGrowthYearly, result, chartData };
}

export function useInflationCalculator() {
  const [currentExpense, setCurrentExpense] = useState(10000);
  const [inflationRate, setInflationRate] = useState(7);
  const [years, setYears] = useState(30);

  const result = useMemo(() => {
    const futureCost = currentExpense * Math.pow(1 + inflationRate / 100, years);
    return { futureCost };
  }, [currentExpense, inflationRate, years]);

  return { currentExpense, setCurrentExpense, inflationRate, setInflationRate, years, setYears, result };
}

export function useSWPCalculator() {
  const [swpCorpus, setSwpCorpus] = useState(2500000);
  const [swpWithdrawal, setSwpWithdrawal] = useState(10000);
  const [swpReturn, setSwpReturn] = useState(8);
  const [swpIncreaseWithdrawal, setSwpIncreaseWithdrawal] = useState(false);
  const [swpYearlyIncrease, setSwpYearlyIncrease] = useState(10);
  const [swpStartAfterYears, setSwpStartAfterYears] = useState(0);
  const [swpHorizonYears, setSwpHorizonYears] = useState(15);

  const result = useMemo(() => {
    const mr = swpReturn / 12 / 100;
    // Full life simulation (until balance depleted) - withdraw first, then interest
    let balance = swpCorpus, totalWithdrawnLife = 0, months = 0, currentWithdrawal = swpWithdrawal;
    const startDelayMonths = swpStartAfterYears * 12;
    for (let i = 0; i < startDelayMonths; i++) balance *= (1 + mr);
    const balanceData = []; let fullInstallmentsLife = 0;
    while (balance > 0 && months < 600) {
      balanceData.push({ month: months, year: Math.floor(months / 12), balance: Math.round(balance) });
      if (swpIncreaseWithdrawal && months > 0 && months % 12 === 0) currentWithdrawal *= (1 + swpYearlyIncrease / 100);
      if (balance >= currentWithdrawal) { balance -= currentWithdrawal; totalWithdrawnLife += currentWithdrawal; fullInstallmentsLife++; balance *= (1 + mr); }
      else { totalWithdrawnLife += balance; balance = 0; }
      months++;
    }
    // Period-end calculation: grow for startDelay, then withdraw for (horizon - startDelay) months
    let periodEndBalance = swpCorpus;
    let withdrawnInHorizon = 0;
    let horizonInstallments = 0;
    currentWithdrawal = swpWithdrawal;
    for (let i = 0; i < startDelayMonths; i++) periodEndBalance *= (1 + mr);
    const withdrawalMonths = Math.max(0, swpHorizonYears - swpStartAfterYears) * 12;
    for (let i = 0; i < withdrawalMonths && periodEndBalance > 0; i++) {
      if (swpIncreaseWithdrawal && i > 0 && i % 12 === 0) currentWithdrawal = swpWithdrawal * Math.pow(1 + swpYearlyIncrease / 100, Math.floor(i / 12));
      const w = swpIncreaseWithdrawal ? currentWithdrawal : swpWithdrawal;
      if (periodEndBalance >= w) { periodEndBalance -= w; withdrawnInHorizon += w; horizonInstallments++; periodEndBalance *= (1 + mr); }
    }
    const today = new Date(); const lastDate = new Date(today);
    lastDate.setMonth(lastDate.getMonth() + startDelayMonths + horizonInstallments);
    const corpusAfterDelay = swpCorpus * Math.pow(1 + mr, startDelayMonths);
    const annualYield = corpusAfterDelay * (swpReturn / 100);
    const annualWithdrawal = swpWithdrawal * 12;
    const isPerpetual = annualYield >= annualWithdrawal;
    return { monthsLasting: months, yearsLasting: months / 12, totalWithdrawn: totalWithdrawnLife, finalBalance: Math.max(0, balance), balanceData: balanceData.filter((_, i) => i % 12 === 0), periodEndValue: Math.round(periodEndBalance), fullInstallments: horizonInstallments, lastInstallmentDate: lastDate, withdrawnInHorizon: Math.round(withdrawnInHorizon), isPerpetual };
  }, [swpCorpus, swpWithdrawal, swpReturn, swpIncreaseWithdrawal, swpYearlyIncrease, swpStartAfterYears, swpHorizonYears]);

  return { swpCorpus, setSwpCorpus, swpWithdrawal, setSwpWithdrawal, swpReturn, setSwpReturn, swpIncreaseWithdrawal, setSwpIncreaseWithdrawal, swpYearlyIncrease, setSwpYearlyIncrease, swpStartAfterYears, setSwpStartAfterYears, swpHorizonYears, setSwpHorizonYears, result };
}
